import React from 'react';
import useQueryAssistantMutation, { DecisionTreeReportQuery } from './useQueryAssistantMutation';

interface HookResult {
  text: string | null;
  mutate(args: { id: string, treeRun: DecisionTreeReportQuery }): void;
  loading: boolean;
}

export default function useStreamText(onEnd: (id: string, text: string) => void): HookResult {
  const [text, setText] = React.useState<string | null>(null);

  const mutation = useQueryAssistantMutation();
  const mutate = React.useCallback((args: { id: string, treeRun: DecisionTreeReportQuery }) => {
    setText(null);

    mutation.mutate(args, {
      onSuccess: async (reader) => {
        const decoder = new TextDecoder();
        let result = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
          if (done) break;

          const decodedValues = decoder.decode(value);
          const arrayified = `[${decodedValues.trim().split('}{').join('},{')}]`;
          const chunks: string[] = [];
          try {
            const jsonsString = JSON.parse(arrayified) as { text: string }[];
            jsonsString.forEach((jsonString) => {
              if (jsonString.text) {
                chunks.push(jsonString.text);
              }
            });
          } catch (e) {
            console.error(`Error while parsing JSON: ${arrayified}`, e);
          }

          const newString = chunks.join('');
          result += newString;
          setText((prevText) => {
            if (prevText) return prevText + newString;
            return newString;
          });
        }
        setText(null);
        onEnd(args.id, result);
      },
      onError: (error) => {
        console.error('Error while reading from stream:', error);
      },
    });
  }, [mutation]);

  return { text, mutate, loading: mutation.isLoading };
}
